<script>
/**
 * @component WhatsappNotificationsConfig
 * @description ABM de NOTIFICACIONES outbound por WhatsApp (recetas,
 *              órdenes médicas, confirmación de turnos). Independiente
 *              del admin de MONITOREO (`WhatsappConfig.vue`) que gobierna
 *              tasks vital/checkin/medication + LLM.
 *
 *              Permite cargar credenciales 2chat propias (opcional: si
 *              vacías el backend hereda las de monitoreo), un toggle
 *              global enabled, y dos toggles por grupo de notificación
 *              (recetas+órdenes / turnos).
 *
 *              Patrón de form + hydrate + apiKey-mascarado espejado de
 *              `WhatsappConfig.vue` para mantener consistencia.
 *
 * @example <WhatsappNotificationsConfig />
 */
import whatsappNotificationsConfigService from "@/services/whatsappNotificationsConfig";

export default {
  name: "WhatsappNotificationsConfig",

  data() {
    return {
      loading: false,
      saving: false,
      config: null,

      // Form local — se hidrata desde config en mounted
      form: {
        credentials: {
          enabled: false,
          virtualNumber: "",
          apiKey: "",
          hasApiKey: false,
        },
        types: {
          indications: { enabled: true },
          appointments: { enabled: true },
        },
      },

      // Modal de prueba
      testDialog: {
        visible: false,
        toPhone: "",
        text: "",
        sending: false,
        result: null,
      },
    };
  },

  async mounted() {
    await this.loadConfig();
  },

  methods: {
    /**
     * @description Carga config actual desde backend y la hidrata en el form.
     */
    async loadConfig() {
      this.loading = true;
      try {
        const { data } = await whatsappNotificationsConfigService.getConfig();
        this.config = data;
        this.hydrateFormFromConfig(data);
      } catch (e) {
        this.$message.error("No se pudo cargar la configuración");
      } finally {
        this.loading = false;
      }
    },

    /**
     * @description Mergea la data del backend en el form local. El apiKey
     *              NO viene crudo — solo el flag `hasApiKey` que muestra
     *              "••••" en el input cuando ya hay uno cargado.
     * @param {Object} data
     */
    hydrateFormFromConfig(data) {
      const c = data?.credentials || {};
      const t = data?.types || {};
      this.form.credentials = {
        enabled: !!c.enabled,
        virtualNumber: c.virtualNumber || "",
        apiKey: "",
        hasApiKey: !!c.hasApiKey,
      };
      this.form.types = {
        indications: { enabled: t.indications?.enabled !== false },
        appointments: { enabled: t.appointments?.enabled !== false },
      };
    },

    /**
     * @description Guarda config. Si `apiKey` vino vacío y ya había uno
     *              cargado, el backend preserva el valor existente.
     */
    async save() {
      this.saving = true;
      try {
        const payload = {
          credentials: {
            enabled: this.form.credentials.enabled,
            virtualNumber: this.form.credentials.virtualNumber.trim(),
            apiKey: this.form.credentials.apiKey.trim(),
          },
          types: this.form.types,
        };
        const { data } = await whatsappNotificationsConfigService.saveConfig(
          payload
        );
        this.config = data;
        this.hydrateFormFromConfig(data);
        this.$message.success("Configuración guardada");
      } catch (e) {
        const msg = e.response?.data?.message || "Error al guardar";
        this.$message.error(msg);
      } finally {
        this.saving = false;
      }
    },

    /**
     * @description Abre el modal de prueba.
     */
    openTestDialog() {
      this.testDialog = {
        visible: true,
        toPhone: "",
        text: "Prueba de notificación WhatsApp desde Quantux.",
        sending: false,
        result: null,
      };
    },

    /**
     * @description Manda el mensaje de prueba.
     */
    async sendTest() {
      if (!this.testDialog.toPhone.trim()) {
        this.$message.warning("Cargá un teléfono destino");
        return;
      }
      this.testDialog.sending = true;
      this.testDialog.result = null;
      try {
        const { data } = await whatsappNotificationsConfigService.testMessage({
          toPhone: this.testDialog.toPhone.trim(),
          text: this.testDialog.text.trim(),
        });
        this.testDialog.result = data;
      } catch (e) {
        this.testDialog.result = {
          error: e.response?.data?.message || e.message,
        };
      } finally {
        this.testDialog.sending = false;
      }
    },
  },
};
</script>

<template lang="pug">
  .wsp-notif-config(v-loading="loading")
    p.wsp-notif-config__caption
      | Configurá el envío automático por WhatsApp de recetas, órdenes médicas
      | y confirmación de turnos. Es un canal extra al email — no reemplaza
      | el envío por mail.

    //- Sección 1: Credenciales
    .wsp-notif-config__section
      h3.wsp-notif-config__section-title Credenciales 2chat
      p.wsp-notif-config__section-hint
        | Si dejás estos campos vacíos, las notificaciones usan el mismo
        | número virtual y apiKey configurados en "Configuración WhatsApp"
        | (monitoreo de pacientes).

      .wsp-notif-config__field
        label.wsp-notif-config__label Número virtual
        el-input(
          v-model="form.credentials.virtualNumber"
          placeholder="+541152799761 (dejar vacío para heredar de monitoreo)"
          clearable
        )

      .wsp-notif-config__field
        label.wsp-notif-config__label API Key
        el-input(
          v-model="form.credentials.apiKey"
          :placeholder="form.credentials.hasApiKey ? '•••••••••• (cargada — dejar vacío para no cambiar)' : 'Pegá tu API key de 2chat'"
          show-password
        )

      .wsp-notif-config__field
        el-switch(v-model="form.credentials.enabled")
        span.wsp-notif-config__switch-label
          | Activar envío de notificaciones por WhatsApp (apagado = nada se manda)

      .wsp-notif-config__field
        el-button(size="small" @click="openTestDialog") Enviar mensaje de prueba

    //- Sección 2: Tipos
    .wsp-notif-config__section
      h3.wsp-notif-config__section-title Tipos de notificación
      p.wsp-notif-config__section-hint
        | Habilitá los grupos que querés que se manden por WhatsApp.
        | Aunque estén apagados, el envío por email sigue funcionando normalmente.

      .wsp-notif-config__field
        el-switch(v-model="form.types.indications.enabled")
        span.wsp-notif-config__switch-label
          | Recetas y órdenes médicas (lab, imágenes, prácticas)

      .wsp-notif-config__field
        el-switch(v-model="form.types.appointments.enabled")
        span.wsp-notif-config__switch-label
          | Confirmación de turnos

    //- Footer
    .wsp-notif-config__footer
      el-button(type="primary" :loading="saving" @click="save") Guardar configuración

    //- Modal de prueba
    el-dialog(
      title="Enviar mensaje de prueba"
      :visible.sync="testDialog.visible"
      width="480px"
    )
      .wsp-notif-config__field
        label.wsp-notif-config__label Teléfono destino *
        el-input(
          v-model="testDialog.toPhone"
          placeholder="+5491132583507"
        )
      .wsp-notif-config__field
        label.wsp-notif-config__label Texto
        el-input(
          v-model="testDialog.text"
          type="textarea"
          :rows="3"
        )
      .wsp-notif-config__test-result(v-if="testDialog.result")
        template(v-if="testDialog.result.success")
          .wsp-notif-config__success
            | ✓ Mensaje enviado. ID: {{ testDialog.result.externalMessageId || '—' }}
          .wsp-notif-config__note(v-if="testDialog.result.inheritedFromMonitoring")
            | Se usaron las credenciales heredadas de "Monitoreo" (no hay
            | credenciales propias cargadas).
        template(v-else)
          .wsp-notif-config__error
            | ✗ {{ testDialog.result.error }}
      span.dialog-footer(slot="footer")
        el-button(@click="testDialog.visible = false") Cerrar
        el-button(type="primary" :loading="testDialog.sending" @click="sendTest") Enviar
</template>

<style lang="scss" scoped>
.wsp-notif-config {
  &__caption {
    margin: 0 0 20px;
    color: #6b7280;
    font-size: 13px;
    line-height: 1.5;
  }

  &__section {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 18px 22px;
    margin-bottom: 16px;
  }

  &__section-title {
    margin: 0 0 6px;
    font-size: 15px;
    font-weight: 600;
    color: #111827;
  }

  &__section-hint {
    margin: 0 0 16px;
    color: #6b7280;
    font-size: 12px;
    line-height: 1.5;
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 14px;

    // Cuando el field contiene un switch + label, los alineamos en row
    &:has(.el-switch) {
      flex-direction: row;
      align-items: center;
      gap: 10px;
    }
  }

  &__label {
    font-size: 13px;
    font-weight: 500;
    color: #374151;
  }

  &__switch-label {
    font-size: 13px;
    color: #374151;
  }

  &__footer {
    margin-top: 18px;
    display: flex;
    justify-content: flex-start;
  }

  &__test-result {
    margin-top: 12px;
    padding: 10px 12px;
    border-radius: 6px;
    font-size: 13px;
  }

  &__success {
    background: #d1fae5;
    color: #065f46;
    padding: 6px 8px;
    border-radius: 4px;
  }

  &__error {
    background: #fee2e2;
    color: #991b1b;
    padding: 6px 8px;
    border-radius: 4px;
  }

  &__note {
    margin-top: 6px;
    font-size: 11px;
    color: #6b7280;
    line-height: 1.4;
  }
}
</style>
